import { gql } from '@apollo/client';
import React from 'react';
import moment from 'moment';
import VerifyPostModal from './VerifyPostModal';
import type { PostVerifyFooter_PostFragment } from '../graphql/generated/graphqlTypes';
import { useAppDispatch } from '../app/redux/useAppSelector';
import yearSensitiveDateString from '../utils/yearSensitiveDateString';
import Button from '../components/Button';
import UIActions from '../ui/UIActions';
import postPath from '../utils/url/postPath';
import {
  calculateExpirationTimestamp,
  humanReadableFrequency,
} from './verificationDateHelpers';
import styles from './PostVerifyFooter.module.scss';
import useNavigate from '../utils/router/useNavigate';
import VerifiedIcon from './VerifiedIcon';
import { VersionChangesFooter } from './history/VersionChanges';

const POST_VERIFY_FOOTER_POST_FRAGMENT = gql`
  fragment PostVerifyFooter_post on Post {
    id
    ...VerifyPostModal_post
    verification {
      id
      unit
      quantity
    }
  }
  ${VerifyPostModal.fragments.post}
`;

interface PostVerifyFooterProps {
  post: PostVerifyFooter_PostFragment;
}

const PostVerifyFooter = ({ post }: PostVerifyFooterProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { verification } = post;
  if (!verification) return null;

  const newExpirationDateString = yearSensitiveDateString(
    calculateExpirationTimestamp(moment().valueOf(), verification).unsafe(),
    'MMM D',
  );

  return (
    <VersionChangesFooter
      header={
        <div className={styles.label}>
          <VerifiedIcon variant="success" />
          <span>Reverify post</span>
        </div>
      }
    >
      <div className={styles.ctaContainer}>
        <div>
          Confirm its accuracy to verify <br /> for{' '}
          {humanReadableFrequency(verification)}, until{' '}
          {newExpirationDateString}
        </div>

        <Button
          onClick={() =>
            dispatch(
              UIActions.openModal(VerifyPostModal, {
                post,
                close() {
                  navigate(postPath({ id: post.id, title: '' }));
                  dispatch(UIActions.closeModal());
                },
              }),
            )
          }
          variant="secondary"
        >
          Reverify
        </Button>
      </div>
    </VersionChangesFooter>
  );
};

PostVerifyFooter.fragments = {
  post: POST_VERIFY_FOOTER_POST_FRAGMENT,
};

export default PostVerifyFooter;
